<template>
  <div>
    <!-- فیلترها -->
    <div class="comments-filters">
      <b-form-input v-model.trim="search" placeholder="جستجوی زبان‌آموز یا متن نظر" class="comments-filters__search" />
      <b-form-select v-model="teacherId" :options="teacherOptions" />
      <b-form-select v-model="lessonId" :options="lessonOptions" />
    </div>

    <p v-if="isFetching" class="comments-progress">
      <b-spinner small class="mr-50" />
      در حال دریافت نظرها… {{ progress.done }} از {{ progress.total }} زبان‌آموز
    </p>

    <b-overlay :show="isLoading" rounded="lg">
      <p v-if="!isLoading && comments.length" class="comments-count">
        {{ filtered.length }} نظر
        <template v-if="filtered.length !== comments.length">از {{ comments.length }}</template>
      </p>

      <div v-if="!isLoading && !isFetching && !comments.length" class="support-empty">
        <feather-icon icon="MessageSquareIcon" size="28" />
        <span>هنوز استادی برای زبان‌آموزها نظری ثبت نکرده است.</span>
      </div>

      <div v-else-if="!isLoading && !filtered.length" class="support-empty">
        <feather-icon icon="FilterIcon" size="28" />
        <span>نظری با این فیلترها پیدا نشد.</span>
      </div>

      <ul v-else class="comments-list">
        <li v-for="comment in filtered" :key="comment.id" class="comment-card">
          <div class="comment-card__head">
            <b-avatar variant="light-primary" size="34" :text="(comment.studentFullName || '؟').charAt(0)" />
            <div class="comment-card__people">
              <strong>{{ comment.studentFullName || `زبان‌آموز #${comment.studentId}` }}</strong>
              <span>
                <feather-icon icon="UserIcon" size="12" />
                {{ comment.teacherFullName || `استاد #${comment.teacherId}` }}
              </span>
            </div>
            <b-badge variant="light-danger" class="comment-card__lesson">
              {{ comment.lessonTitle || `درس ${comment.lessonId}` }}
            </b-badge>
            <span class="comment-card__date">{{ formatDate(comment.createDate) }}</span>
          </div>
          <p class="comment-card__text">{{ comment.message }}</p>
        </li>
      </ul>
    </b-overlay>
  </div>
</template>

<script>
import { BOverlay, BAvatar, BBadge, BFormInput, BFormSelect, BSpinner } from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { GetSupportAdminList, GetSupportCommentsByStudent, SUPPORT_STATUS } from "@/libs/Api/SupportLesson";
import { formatDate } from "./supportFormat";

const PAGE_SIZE = 100;
// چند درخواست نظر هم‌زمان
const CONCURRENCY = 6;
// نظر فقط برای درخواست‌هایی ثبت می‌شود که به استاد وصل شده‌اند (فعال، منقضی یا تمام‌شده)
const STATUSES_WITH_COMMENTS = [SUPPORT_STATUS.assigned, SUPPORT_STATUS.expired, SUPPORT_STATUS.endTime];

export default {
  name: "SupportComments",
  components: { BOverlay, BAvatar, BBadge, BFormInput, BFormSelect, BSpinner },
  data() {
    return {
      isLoading: false,
      comments: [],
      search: "",
      teacherId: null,
      lessonId: null,
      // پیشرفت دریافت نظرها: { done, total }
      progress: null,
    };
  },
  computed: {
    teacherOptions() {
      return [{ value: null, text: "همه‌ی اساتید" }, ...this.uniqueOptions("teacherId", "teacherFullName", "استاد")];
    },
    lessonOptions() {
      return [{ value: null, text: "همه‌ی درس‌ها" }, ...this.uniqueOptions("lessonId", "lessonTitle", "درس")];
    },
    isFetching() {
      return Boolean(this.progress && this.progress.done < this.progress.total);
    },
    filtered() {
      const query = this.search.toLowerCase();
      return this.comments.filter((comment) => {
        if (this.teacherId && comment.teacherId !== this.teacherId) return false;
        if (this.lessonId && comment.lessonId !== this.lessonId) return false;
        if (!query) return true;
        return [comment.studentFullName, comment.message].some((value) =>
          String(value || "").toLowerCase().includes(query)
        );
      });
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    formatDate,
    uniqueOptions(idKey, titleKey, fallback) {
      const map = new Map();
      this.comments.forEach((comment) => {
        if (comment[idKey] && !map.has(comment[idKey])) {
          map.set(comment[idKey], comment[titleKey] || `${fallback} ${comment[idKey]}`);
        }
      });
      return [...map].map(([value, text]) => ({ value, text }));
    },
    // زبان‌آموزهایی که درخواستشان به استاد وصل شده (درخواست‌های پرداخت‌نشده نظری ندارند)
    async fetchStudentIdsByStatus(status) {
      const ids = new Set();
      let page = 1;
      let total = 0;
      do {
        const req = new GetSupportAdminList(this);
        req.setRequestParam({ count: PAGE_SIZE, pageNumber: page, status });
        // eslint-disable-next-line no-await-in-loop
        await req.get((res) => {
          const data = (res && res.data) || {};
          total = Number(data.total || 0);
          (data.data || []).forEach((row) => ids.add(row.studentId));
        });
        page += 1;
      } while ((page - 1) * PAGE_SIZE < total);
      return ids;
    },
    async fetchStudentIds() {
      const sets = await Promise.all(STATUSES_WITH_COMMENTS.map((status) => this.fetchStudentIdsByStatus(status)));
      return [...new Set(sets.flatMap((set) => [...set]))];
    },
    fetchComments(studentId) {
      return new Promise((resolve) => {
        const req = new GetSupportCommentsByStudent(this);
        req.setRequestParam({ studentId });
        req.get(
          (res) => resolve((res && res.isSuccess && res.data) || []),
          () => resolve([])
        );
      });
    },
    // API همه‌ی نظرها یکجا وجود ندارد؛ نظرهای هر زبان‌آموز جدا گرفته می‌شود.
    // چند درخواست هم‌زمان و نمایش تدریجی تا صفحه منتظر همه نماند
    async load() {
      this.isLoading = true;
      this.comments = [];
      this.progress = null;
      try {
        const studentIds = await this.fetchStudentIds();
        this.progress = { done: 0, total: studentIds.length };
        this.isLoading = false;
        const queue = [...studentIds];
        const worker = async () => {
          while (queue.length) {
            const id = queue.shift();
            // eslint-disable-next-line no-await-in-loop
            const list = await this.fetchComments(id);
            if (list.length) {
              this.comments = [...this.comments, ...list].sort(
                (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
              );
            }
            this.progress.done += 1;
          }
        };
        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, studentIds.length) }, worker));
      } catch (error) {
        console.error(error);
        this.$toast({
          component: ToastificationContent,
          props: { title: "دریافت نظرات انجام نشد", icon: "AlertTriangleIcon", variant: "danger" },
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped src="./supportList.css"></style>
<style scoped>
.comments-filters {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.comments-progress {
  margin-bottom: 0.75rem;
  font-size: 0.82rem;
  color: #82868b;
}

.comments-count {
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: #82868b;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.comment-card {
  padding: 0.9rem 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 12px;
}

.comment-card__head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-bottom: 0.5rem;
}

.comment-card__people {
  display: flex;
  flex-direction: column;
  flex: 1 1 160px;
  min-width: 0;
  line-height: 1.5;
}

.comment-card__people span {
  font-size: 0.78rem;
  color: #82868b;
}

.comment-card__lesson {
  max-width: 100%;
  white-space: normal;
}

.comment-card__date {
  font-size: 0.78rem;
  color: #b9b9c3;
}

/* نظر طولانی: ارتفاع ثابت و اسکرول داخل همان باکس */
.comment-card__text {
  max-height: 9rem;
  overflow-y: auto;
  padding-left: 0.4rem;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.9;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

.dark-layout .comment-card {
  border-color: #3b4253;
}

@media (max-width: 767.98px) {
  .comments-filters {
    grid-template-columns: 1fr;
  }
}
</style>
